(ns app.bundle-social.page
  "Replicant page that lists the social accounts connected through
   bundle.social and runs the connect flow: for Facebook a hint modal
   that asks whether the page is managed in Meta Business Suite
   (business scope) or personally (standard scope), then the platform's
   own OAuth, and a page picker when the OAuth shared several pages.

   Offers a connect button per launched platform of
   `app.bundle-social.platforms`; `?platform=<id>` adds an unlaunched
   one for the internal test."
  (:require [app.bundle-social.platforms :as platforms]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [ui.app-frame :as app-frame]))

(def path
  "/ui/social-accounts")

(def accounts-query
  {:query/kind :query/social-accounts})

(def post-limits-query
  "The posts per day and account by platform, see
   `app.bundle-social.post-limits`; cached on the server, so loading it
   does not reach bundle.social."
  {:query/kind :query/social-post-limits})

(defn post-limit
  "The posts per day of an account of `platform`, once loaded."
  [state platform]
  (get (:social-accounts/post-limits (query/get-result state post-limits-query))
       (:social-platform/id platform)))

(defn connect-command
  "`return-path` is the page the OAuth returns to, the accounts page
   when nil (see `app.bundle-social.connect-command/return-paths`)."
  ([platform business?]
   (connect-command platform business? nil))
  ([platform business? return-path]
   {:command/kind :command/bundle-social-connect
    :command/data (cond-> {:platform platform
                           :business? business?}
                    return-path (assoc :return-path return-path))}))

(defn sync-command
  [team-id]
  {:command/kind :command/social-accounts-sync
   :command/data {:bundle-social/team-id team-id}})

(defn set-channel-command
  "Connects the pages `channel-ids`: the first on the OAuth team, every
   further one on a team of its own."
  [team-id platform channel-ids]
  {:command/kind :command/social-account-set-channel
   :command/data {:bundle-social/team-id team-id
                  :platform platform
                  :channel-ids (vec channel-ids)}})

(defn delete-command
  "Removes a row: an account of bundle.social (and the same YouTube
   channel of the own integration, see the command), or an account of
   Storrito's own YouTube or TikTok integration with its own command."
  [account]
  (if-let [uuid (:social.account/uuid account)]
    {:command/kind :command/social-account-delete
     :command/data {:social.account/uuid uuid}}
    (let [{:keys [delete-command id-key]} (:social-platform/own-integration
                                           (platforms/by-id (:social.account/platform account)))]
      {:command/kind delete-command
       :command/data {id-key (:social.account/own-id account)}})))

(defn row-key
  "A row's key: the bundle.social account's uuid, or the platform and
   id of an account of the own integration."
  [account]
  (or (:social.account/uuid account)
      (str (:social.account/platform account) "-" (:social.account/own-id account))))

(defn- platform-icon
  [platform classes]
  (or (platforms/icon platform classes)
      ;; A network Storrito does not support: its initial.
      [:span {:class (into ["flex" "items-center" "justify-center" "text-xs"
                            "font-semibold" "text-gray-500"]
                           classes)}
       (subs (str platform " ") 0 1)]))

(defn connecting?
  "A connect command of any platform is under way, or its OAuth url is
   about to be opened. The business scope is true or false for Facebook
   and nil for every other platform."
  [state]
  (boolean
    (or (::leaving? state)
        (some #(command/issued? state %)
              (for [platform platforms/platforms
                    business? [true false nil]]
                (connect-command (:social-platform/id platform) business?))))))

(defn offered-platforms
  "The launched platforms, plus the unlaunched one of `?platform=`."
  [state]
  (let [preview (platforms/by-id (::preview-platform state))]
    (cond-> (vec (platforms/launched))
      (and preview
           (not (:social-platform/launched? preview))
           (not (platforms/hidden? (:social-platform/id preview))))
      (conj preview))))

(defn own-connect-url
  "The OAuth url of Storrito's own integration of the platform
   (`:social-accounts/own-connect-urls`), nil when it is not configured
   on this server."
  [state platform-id]
  (get-in (query/get-result state accounts-query)
          [:social-accounts/own-connect-urls platform-id]))

(defn- connect-button
  "The platform's connect button in its card header: the connect flow of
   bundle.social, or with `own-url` the OAuth of Storrito's own
   integration (YouTube, TikTok until their launch). `busy?` while a
   connect or the sync of a new connection runs; `unavailable?` when the
   own integration is not configured on this server."
  [platform {:keys [busy? own-url unavailable?]}]
  (let [attrs {:class (into ["inline-flex" "items-center" "gap-1.5" "px-3" "py-1.5"
                             "text-xs" "font-semibold" "rounded-md" "cursor-pointer" "shrink-0"
                             "focus-visible:outline" "focus-visible:outline-2"
                             "focus-visible:outline-offset-2" "focus-visible:outline-blue-600"
                             "disabled:opacity-60" "disabled:cursor-not-allowed"]
                            (get-in platform [:social-platform/connect-card :style :button-class]))
               :aria-label (get-in platform [:social-platform/connect-card :button-text])}
        label (list [:span {:class ["text-sm" "leading-none"]} "+"] "Connect")]
    (cond
      own-url
      (into [:a (assoc attrs :href own-url)] label)

      unavailable?
      (into [:button (assoc attrs
                            :type "button"
                            :disabled true
                            :title "Not available on this server.")]
            label)

      :else
      (into [:button (assoc attrs
                            :type "button"
                            :disabled busy?
                            :on {:click [[:social-accounts/connect (:social-platform/id platform)]]})]
            label))))

(defn- avatar
  "The page's picture, or the platform icon when there is none or the
   url stopped working (bundle.social keeps the pictures in its own
   storage and may rotate the file on a profile refresh)."
  [state account]
  (let [uuid (row-key account)
        url (:social.account/avatar-url account)
        broken? (get-in state [::broken-avatars uuid])]
    [:div
     {:class ["w-9" "h-9" "rounded-full" "overflow-hidden" "bg-gray-100"
              "shrink-0" "ring-1" "ring-gray-200"]}
     (if (and url (not broken?))
       [:img {:src url
              :alt ""
              :class ["w-full" "h-full" "object-cover"]
              :on {:error [[:store/assoc-in [::broken-avatars uuid] true]]}}]
       [:div {:class (into ["w-full" "h-full" "flex" "items-center" "justify-center"]
                           (:social-platform/text-class
                             (platforms/by-id (:social.account/platform account))))}
        (platform-icon (:social.account/platform account) ["w-4" "h-4"])])]))

(defn- classic-badge
  "Marks an account of Storrito's own integration in a card that lists
   accounts of the new connection as well, which may be the same
   account (TikTok cannot be matched)."
  []
  [:span
   {:class ["ml-1.5" "inline-flex" "items-center" "rounded" "bg-gray-100" "px-1.5"
            "py-0.5" "align-middle" "text-[10px]" "font-medium" "uppercase"
            "tracking-wide" "text-gray-600"]
    :title "Connected with Storrito's original connection"}
   "Classic"])

(defn classic?
  "An account of the own integration among `accounts` of the new
   connection."
  [account accounts]
  (and (not (:social.account/uuid account))
       (boolean (some :social.account/uuid accounts))))

(def reconnect-class
  ["px-2.5" "py-1" "text-xs" "font-semibold" "text-white"
   "bg-blue-600" "rounded-md" "hover:bg-blue-700" "cursor-pointer"
   "focus-visible:outline" "focus-visible:outline-2"
   "focus-visible:outline-offset-2" "focus-visible:outline-blue-600"
   "disabled:opacity-60" "disabled:cursor-wait"])

(defn disconnected?
  "bundle.social no longer accepts the account, or the tokens of an
   account of the own integration are known to be unusable."
  [account]
  (or (some? (:social.account/disconnected-at account))
      (boolean (:social.account/own-disconnected? account))))

(defn- reconnect-button
  "bundle.social's connect flow, or the own OAuth for an account of the
   own integration (nil when it is not configured on this server)."
  [state account busy?]
  (if (:social.account/uuid account)
    [:button
     {:type "button"
      :class reconnect-class
      :disabled busy?
      :on {:click [[:social-accounts/connect (:social.account/platform account)]]}}
     "Reconnect"]
    (when-let [url (own-connect-url state (:social.account/platform account))]
      [:a
       {:href url
        :class reconnect-class
        :aria-label (str "Reconnect " (:social.account/name account))}
       "Reconnect"])))

(defn- account-row
  [state account busy? classic]
  (let [cmd (delete-command account)
        deleting (command/issued? state cmd)
        disconnected (disconnected? account)]
    [:li
     {:class ["flex" "items-center" "gap-3" "px-4" "py-2.5"]
      :replicant/key (row-key account)}
     (avatar state account)
     [:div
      {:class ["flex-1" "min-w-0"]}
      [:div
       {:class ["text-sm" "font-medium" "text-gray-900" "truncate"]}
       (:social.account/name account)
       (when classic
         (classic-badge))]
      (when disconnected
        [:div {:class ["text-xs" "font-medium" "text-red-700"]}
         "Disconnected"])]
     (when disconnected
       (reconnect-button state account busy?))
     [:button
      {:type "button"
       :class ["px-2" "py-1" "text-xs" "font-medium" "text-gray-500" "rounded-md"
               "hover:text-red-600" "hover:bg-red-50" "transition-colors" "cursor-pointer"
               "focus-visible:outline" "focus-visible:outline-2" "focus-visible:outline-red-500"
               "disabled:cursor-wait"]
       :aria-label (str "Remove " (:social.account/name account))
       :disabled deleting
       :on {:click [[:js/confirm (str "Remove " (:social.account/name account) " from Storrito?")]
                    [:data/command
                     cmd
                     {:on-success [[:data/query accounts-query]]
                      :on-error [[:ui/show-error]]}]]}}
      (if deleting "Removing..." "Remove")]]))

(defn- account-list
  [state accounts busy?]
  [:ul
   {:class ["divide-y" "divide-gray-100" "border-t" "border-gray-100"]}
   (map #(account-row state % busy? (classic? % accounts)) accounts)])

(def card-class
  ["relative" "mb-4" "break-inside-avoid" "overflow-hidden" "rounded-xl"
   "border" "border-gray-200" "bg-white" "shadow-sm"])

(defn- platform-card
  "One network: a rail in its brand colour, its icon, what it posts, how
   many accounts are connected and its connect button, then the
   accounts."
  [state platform accounts busy? offered?]
  (let [id (:social-platform/id platform)
        card (:social-platform/connect-card platform)
        heading-id (str "social-accounts-" id)
        ;; Otherwise the card connects through the own integration.
        bundle-social? (or offered?
                           (not (:social-platform/own-integration platform))
                           ;; Connected through bundle.social during an internal test.
                           (some :social.account/uuid accounts))
        ;; Also for the own integrations, which bundle.social replaces.
        limit (post-limit state platform)]
    [:section
     {:class card-class
      :replicant/key id
      :aria-labelledby heading-id}
     [:div {:class (into ["h-1"] (or (get-in card [:style :rail-class]) ["bg-gray-400"]))}]
     [:header
      {:class ["flex" "items-center" "gap-3" "px-4" "pt-3" "pb-3"]}
      [:div
       {:class (into ["flex" "h-9" "w-9" "shrink-0" "items-center" "justify-center"
                      "rounded-lg" "ring-1"]
                     (get-in card [:style :icon-class]))}
       ((:social-platform/icon platform) ["h-4" "w-4"])]
      [:div
       {:class ["min-w-0" "flex-1"]}
       [:h2 {:id heading-id
             :class ["text-sm" "font-semibold" "text-gray-900"]}
        (:social-platform/name platform)]
       [:div {:class ["text-xs" "text-gray-500" "truncate"]}
        (:eyebrow card)
        (when (seq accounts)
          (str " · " (platforms/count-label platform (count accounts))))]
       ;; A line of its own: the line above is truncated.
       (when limit
         [:div {:class ["text-xs" "text-gray-500"]}
          (str "Up to " limit (if (= 1 limit) " post" " posts") " per day per account")])]
      (if bundle-social?
        (connect-button platform {:busy? busy?})
        (let [url (own-connect-url state id)]
          (connect-button platform {:own-url url
                                    :unavailable? (nil? url)})))]
     (if (seq accounts)
       (account-list state accounts busy?)
       [:p
        {:class ["mx-4" "mb-4" "rounded-lg" "border" "border-dashed" "border-gray-200"
                 "px-3" "py-2.5" "text-xs" "text-gray-500"]}
        (:description card)])]))

(defn- other-accounts-card
  "Accounts of a network Storrito does not support (anymore), so they
   can still be seen and removed."
  [state accounts busy?]
  [:section
   {:class card-class
    :replicant/key "other"
    :aria-labelledby "social-accounts-other"}
   [:div {:class ["h-1" "bg-gray-400"]}]
   [:header
    {:class ["px-4" "pt-3" "pb-3"]}
    [:h2 {:id "social-accounts-other"
          :class ["text-sm" "font-semibold" "text-gray-900"]}
     "Other networks"]
    [:div {:class ["text-xs" "text-gray-500"]}
     "Storrito doesn't post to these networks. Remove them if you don't need them."]]
   (account-list state accounts busy?)])

(defn- platform-grid
  "Without bundle.social configured on this server, only the cards of
   the own YouTube and TikTok integrations are offered."
  [state accounts busy? configured?]
  (let [by-platform (group-by :social.account/platform accounts)
        unknown (platforms/unknown-accounts accounts)
        offered (if configured? (offered-platforms state) [])
        offered-ids (set (map :social-platform/id offered))
        ;; YouTube and TikTok always have a card: until they are
        ;; launched, it connects through the own integration.
        own-only (remove #(offered-ids (:social-platform/id %))
                         (filter :social-platform/own-integration platforms/platforms))]
    [:div
     ;; Columns rather than a grid: a network with one account does not
     ;; leave a gap next to one with many.
     {:class ["columns-1" "lg:columns-2" "gap-4"]}
     (for [platform (platforms/shown-platforms (into offered own-only) accounts)]
       (platform-card state platform (get by-platform (:social-platform/id platform)) busy?
                      (contains? offered-ids (:social-platform/id platform))))
     (when (seq unknown)
       (other-accounts-card state unknown busy?))]))

(defn- not-configured
  []
  [:div
   {:class ["mb-4" "text-sm" "text-amber-700" "bg-amber-50" "border" "border-amber-200"
            "rounded-lg" "px-4" "py-2"]}
   "Some networks are not available on this server."])

(defn- callback-error
  [state]
  (when-let [message (::callback-message state)]
    [:div
     {:class ["mb-4" "text-sm" "text-red-700" "bg-red-50" "border" "border-red-200"
              "rounded-lg" "px-4" "py-2"]}
     message]))

(defn- query-failed
  []
  [:div
   {:class ["text-sm" "text-red-700" "bg-red-50" "border" "border-red-200"
            "rounded-lg" "px-4" "py-2"]}
   "The social accounts could not be loaded."])

(defn syncing?
  "The account the user returned with from the OAuth (`?team=`) is being
   synced, which takes a few seconds (bundle.social, page selection)."
  [state]
  (boolean
    (when-let [team-id (::syncing-team state)]
      (command/issued? state (sync-command team-id)))))

(defn- syncing-panel
  []
  [:div
   {:class ["mb-4" "flex" "items-center" "gap-3" "rounded-lg" "border" "border-blue-200"
            "bg-blue-50" "px-4" "py-3" "text-sm" "text-blue-800"]}
   [:div {:class ["h-5" "w-5" "shrink-0" "animate-spin" "rounded-full" "border-2"
                  "border-blue-200" "border-t-blue-600"]}]
   "Finishing the connection. This takes a few seconds..."])

(defn render
  [state]
  (let [result (query/get-result state accounts-query)
        accounts (:social-accounts/accounts result)
        syncing (syncing? state)
        loading (and (not syncing)
                     (query/loading? state accounts-query)
                     (not (query/available? state accounts-query)))]
    (app-frame/app-frame
      state
      (cond
        loading
        [:div
         {:class ["flex" "items-center" "justify-center" "py-20"]}
         [:div {:class ["text-gray-400"]} "Loading..."]]

        (and (query/error? state accounts-query)
             (not (query/available? state accounts-query)))
        (query-failed)

        :else
        [:div
         {:class ["max-w-5xl"]}
         [:div
          {:class ["mb-5"]}
          [:h1
           {:class ["text-2xl" "font-bold" "text-gray-900"]}
           "Social Accounts"]
          [:p {:class ["mt-1" "text-sm" "text-gray-500"]}
           "The pages and profiles Storrito posts to, by network."]]
         (when (false? (:bundle-social/configured? result))
           (not-configured))
         (callback-error state)
         (when syncing
           (syncing-panel))
         (platform-grid state accounts (or syncing (connecting? state))
                        (not (false? (:bundle-social/configured? result))))]))))

(defn- scope-button
  [title description actions disabled?]
  [:button
   {:type "button"
    :class ["w-full" "text-left" "rounded-lg" "border" "border-gray-200" "bg-white"
            "px-4" "py-3" "hover:border-blue-400" "hover:bg-blue-50" "cursor-pointer"
            "disabled:opacity-60" "disabled:cursor-wait"]
    :disabled disabled?
    :on {:click actions}}
   [:div {:class ["text-sm" "font-semibold" "text-gray-900"]} title]
   [:div {:class ["text-xs" "text-gray-500" "mt-0.5"]} description]])

(defn render-hint-modal
  "Asks how the page is managed, which decides Meta's OAuth scope: a
   page in a Business Portfolio only shows up with the business scope,
   a personally managed page with the standard one. Asked on every
   connect, since the answer differs per page."
  [state]
  (let [platform (get-in state [:ui/modal :params :platform])
        ;; `::leaving?` is set on the click and stays until the browser
        ;; has left for Facebook; the command's issued state alone ends
        ;; with the response, a moment before the redirect.
        opening? (or (::leaving? state)
                     (connecting? state))]
    [:div {:class ["relative"]}
     (when opening?
       [:div {:class ["absolute" "-inset-4" "z-10" "flex" "items-center" "justify-center"
                      "rounded-lg" "bg-white/70"]}
        [:div {:class ["h-10" "w-10" "animate-spin" "rounded-full" "border-4"
                       "border-gray-200" "border-t-blue-600"]}]])
     [:h2 {:class ["text-xl" "font-bold" "text-gray-900" "mb-2"]}
      "Connect a Facebook page"]
     [:p {:class ["text-sm" "text-gray-600" "mb-4"]}
      "Facebook lists your pages in the next step. Choose how the page is managed, so that Facebook shows it:"]
     [:div {:class ["space-y-3"]}
      (scope-button "Managed in Meta Business Suite"
                    "The page belongs to a business portfolio. This is the case for most company pages."
                    [[:social-accounts/start-connect platform true]]
                    opening?)
      (scope-button "Managed personally"
                    "You manage the page from your personal Facebook profile, without a business portfolio."
                    [[:social-accounts/start-connect platform false]]
                    opening?)]
     [:p {:class ["text-xs" "text-gray-500" "mt-4"]}
      "In the Facebook dialog select the page you want to post on and keep all permissions enabled. If Facebook does not list your page, come back and try the other option."]
     [:p {:class ["text-xs" "text-gray-500" "mt-2"]}
      "Facebook shows the connection as bundle.social, the publishing service Storrito uses for Facebook pages."]]))

(defn connected-channel-ids
  "The page ids of the accounts that are connected already, from the
   accounts query; empty until its result is in."
  [state]
  (->> (:social-accounts/accounts (query/get-result state accounts-query))
       (keep :social.account/platform-id)
       (set)))

(defn selected-channels
  "The chosen page ids; until the user changes the ticks, all shared
   pages that are not connected yet."
  [state channels]
  (or (::selected-channels state)
      (let [connected (connected-channel-ids state)]
        (set (remove connected (map :channel/id channels))))))

(defn render-page-select-modal
  "Offers the pages the OAuth shared, all ticked. The choice is sent
   with the set-channel command; only afterwards the accounts can
   publish."
  [state]
  (let [{:keys [channels platform]} (get-in state [:ui/modal :params])
        {:social-platform/keys [name channel-noun channel-nouns]
         :or {name "The platform" channel-noun "page" channel-nouns "pages"}}
        (platforms/by-id platform)
        selected (selected-channels state channels)
        connected (connected-channel-ids state)
        saving? (::selecting-channel? state)
        count-selected (count selected)]
    [:div
     [:h2 {:class ["text-xl" "font-bold" "text-gray-900" "mb-2"]}
      (str "Which " channel-nouns " should Storrito post on?")]
     [:p {:class ["text-sm" "text-gray-600" "mb-4"]}
      (str name " shared these " channel-nouns ". Untick the ones you do not want to connect. "
           "Those that are connected already start unticked; tick one to connect it again.")]
     [:div {:class ["space-y-2"]}
      (for [channel channels
            :let [id (:channel/id channel)
                  ticked? (contains? selected id)]]
        [:label {:replicant/key id
                 ;; One class per string: Replicant hands every entry to
                 ;; classList.add, which throws on a space.
                 :class (-> ["flex" "items-center" "gap-3" "rounded-lg" "border" "px-3" "py-2"]
                            (conj (if saving? "cursor-not-allowed" "cursor-pointer"))
                            (into (if ticked?
                                    ["border-blue-500" "bg-blue-50"]
                                    ["border-gray-200" "bg-white"])))}
         ;; Disabled while the command runs: a tick changed after the
         ;; click would not reach the command, but the modal would show it.
         [:input {:type "checkbox"
                  :class ["h-4" "w-4" "rounded" "border-gray-300" "text-blue-600"]
                  :checked ticked?
                  :disabled saving?
                  :on {:change [[:store/assoc ::selected-channels
                                 (if ticked?
                                   (disj selected id)
                                   (conj selected id))]]}}]
         [:div {:class ["w-8" "h-8" "rounded-full" "overflow-hidden" "bg-gray-100" "shrink-0"]}
          (when (:channel/avatar-url channel)
            [:img {:src (:channel/avatar-url channel)
                   :class ["w-full" "h-full" "object-cover"]}])]
         [:span {:class ["text-sm" "text-gray-900" "grow"]} (:channel/name channel)]
         (when (contains? connected id)
           [:span {:class ["text-xs" "text-gray-500" "shrink-0"]}
            "Already connected"])])]
     [:button
      {:type "button"
       :class ["mt-5" "w-full" "px-4" "py-2" "text-sm" "font-medium" "rounded-lg" "text-white"
               "bg-blue-600" "hover:bg-blue-700" "cursor-pointer" "disabled:opacity-60"
               "disabled:cursor-not-allowed"]
       :disabled (or saving? (zero? count-selected))
       :on {:click [[:social-accounts/select-channels]]}}
      (cond
        saving? "Connecting..."
        (= 1 count-selected) (str "Connect this " channel-noun)
        :else (str "Connect " count-selected " " channel-nouns))]]))

(defn render-platform-hint-modal
  "Tells what the platform needs before its OAuth, from its
   `:social-platform/connect-hint` (Snapchat: a Public Profile; X: its
   prices), with a link to the platform's help and a button that
   continues."
  [state]
  (let [platform-id (get-in state [:ui/modal :params :platform])
        {:keys [return-path username account-type]} (get-in state [:ui/modal :params])
        platform (platforms/by-id platform-id)
        {:keys [title body points account-type-notes help-url help-label]} (:social-platform/connect-hint platform)
        note (get account-type-notes account-type)
        opening? (or (::leaving? state)
                     (connecting? state))]
    [:div {:class ["relative"]}
     (when opening?
       [:div {:class ["absolute" "-inset-4" "z-10" "flex" "items-center" "justify-center"
                      "rounded-lg" "bg-white/70"]}
        [:div {:class ["h-10" "w-10" "animate-spin" "rounded-full" "border-4"
                       "border-gray-200" "border-t-blue-600"]}]])
     [:div {:class ["flex" "items-center" "gap-3" "mb-3"]}
      [:span {:class (:social-platform/text-class platform)}
       ((:social-platform/icon platform) ["h-6" "w-6"])]
      [:h2 {:class ["text-xl" "font-bold" "text-gray-900"]} title]]
     [:p {:class ["text-sm" "text-gray-600"]} body]
     (when (seq points)
       [:ul {:class ["mt-3" "space-y-2" "list-disc" "pl-5" "text-sm" "text-gray-600"]}
        (for [point points]
          [:li {:replicant/key point} point])])
     (when note
       [:p {:class ["mt-3" "rounded-md" "border" "border-amber-200" "bg-amber-50"
                    "px-3" "py-2" "text-sm" "text-amber-800"]}
        note])
     (when username
       [:p {:class ["mt-3" "text-sm" "font-medium" "text-gray-900"]}
        "Log in as @" username " in the " (:social-platform/name platform) " dialog."])
     (when help-url
       [:a {:href help-url
            :target "_blank"
            :rel "noopener noreferrer"
            :class ["mt-2" "block" "text-sm" "font-medium" "text-blue-600"
                    "hover:underline"]}
        help-label])
     [:button
      {:type "button"
       :class (into ["mt-5" "w-full" "px-4" "py-2" "text-sm" "font-medium" "rounded-lg"
                     "cursor-pointer" "disabled:opacity-60" "disabled:cursor-wait"]
                    (get-in platform [:social-platform/connect-card :style :button-class]))
       :disabled opening?
       :on {:click [[:social-accounts/start-connect platform-id nil return-path]]}}
      (str "Continue to " (:social-platform/name platform))]]))

(def hint-modal
  {:modal-id :modals/social-account-connect-hint
   :render #'render-hint-modal})

(def platform-hint-modal
  {:modal-id :modals/social-account-platform-hint
   :render #'render-platform-hint-modal})

(def page-select-modal
  {:modal-id :modals/social-account-page-select
   :render #'render-page-select-modal})

(defn on-load
  "Returning from the OAuth the url carries the new team as `?team=`,
   which is synced while the connected accounts are shown with a
   \"Finishing the connection\" panel; the accounts are queried again
   once the sync is done. A failed connect carries one of the
   `callback-messages` parameters instead."
  [location]
  (let [query-params (:location/query-params location)
        team-id (:team query-params)
        message (platforms/callback-message query-params)]
    (cond-> [[:ui/hide-sidebar-on-mobile]
             [:store/assoc ::callback-message message]
             [:store/assoc ::preview-platform (:platform query-params)]
             [:store/assoc ::syncing-team (not-empty team-id)]
             ;; The connected accounts show while the new one is synced.
             [:data/query accounts-query]
             [:data/query post-limits-query]]
      message
      (conj [:social-accounts/forget-team-param])

      (seq team-id)
      (conj [:social-accounts/sync team-id]))))

(def page
  {:page-id :pages/social-accounts
   ;; Keep in step with `path`.
   :route [["ui" "social-accounts"]]
   :on-load #'on-load
   :render #'render})

(def former-pages
  "The former pages of Storrito's own TikTok and YouTube integrations,
   whose accounts this page lists now: a bookmark of them lands here."
  (for [[page-id segment] [[:pages/tiktok "tiktok"] [:pages/youtube "youtube"]]]
    {:page-id page-id
     :route [["ui" segment]]
     :on-load (fn [_location] [[:social-accounts/open-accounts-page]])
     :render (fn [_state] nil)}))
